"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Check, Leaf, Calendar, MapPin, Info, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
const NativeSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  children,
  ...props
}, ref) => {
  return <div data-section-id="15936" className="relative">
        <select className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none ${className}`} ref={ref} {...props}>
          {children}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4"><path d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
        </div>
      </div>;
});
NativeSelect.displayName = "NativeSelect";
const Checkbox = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input type="checkbox" className={`peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground ${className}`} ref={ref} {...props} />;
});
Checkbox.displayName = "Checkbox";
function LawnMaintenanceForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="h-16 w-16 bg-secondary/20 text-secondary rounded-full flex items-center justify-center mb-2">
          <Check className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-primary">God Bless You!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-secondary text-secondary hover:bg-secondary/10">
          Submit Another Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Your Full Name</FormLabel>
              <FormControl>
                <Input placeholder="St. Sampson" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="companion@faith.org" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="property_type" rules={{
        required: "Please select an affiliation"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Parish or Affiliation</FormLabel>
              <FormControl>
                <NativeSelect {...field}>
                  <option value="" disabled>Select affiliation</option>
                  <option value="Orthodox Parish">Orthodox Parish</option>
                  <option value="Christian Community">Christian Community</option>
                  <option value="General Volunteer">General Volunteer</option>
                  <option value="Other">Other</option>
                </NativeSelect>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="address" rules={{
      required: "Home address is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans">Home Address</FormLabel>
            <FormControl>
              <Input placeholder="Street, City, Zip Code" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_type" rules={{
      required: "Please select your availability"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans">Weekly Availability</FormLabel>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays", "On-Call"].map((service, i) => <div key={service} className="flex items-center space-x-2" data-index={i}>
                  <Checkbox id={`service-${i}`} checked={Array.isArray(field.value) && field.value.includes(service)} onChange={e => {
            const checked = e.target.checked;
            const current = Array.isArray(field.value) ? field.value : [];
            const newValue = checked ? [...current, service] : current.filter((val: string) => val !== service);
            field.onChange(newValue);
          }} />
                  <label htmlFor={`service-${i}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer font-sans">
                    {service}
                  </label>
                </div>)}
            </div>
            <FormDescription className="font-sans">Select the days you are available to visit seniors.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_start_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans">Potential Start Date</FormLabel>
            <FormControl>
              <Input type="date" {...field} />
            </FormControl>
            <FormDescription className="font-sans">When are you ready to begin your orientation?</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans">Why do you wish to serve?</FormLabel>
            <FormControl>
              <Textarea placeholder="Share your heart for the elderly or any special skills (e.g., languages, medical background)..." className="resize-none font-sans" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive font-sans">
          {submitError}
        </div>}

      <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 border-b-2 border-secondary font-sans font-medium" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Submitting Application...
          </> : "Submit Volunteer Application"}
      </Button>
    </Form>;
}
export default function LawnTreatmentMaintenance() {
  const maintenanceTasks = [{
    title: "Compassionate Presence",
    description: "Offering consistent friendship and listening ears to those who may feel forgotten in their own homes."
  }, {
    title: "Spiritual Support",
    description: "Sharing prayer, scripture, and faith-based comfort for seniors seeking spiritual connection."
  }, {
    title: "Basic Needs Checks",
    description: "Assisting with simple tasks and ensuring seniors have food, water, and a safe environment."
  }, {
    title: "Crisis Prevention",
    description: "Acting as a bridge between seniors and community services to prevent emergency welfare situations."
  }];
  return <section id="volunteer-signup-form" className="py-16 lg:py-24 bg-[hsl(30_20%_98%)] text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          <div className="flex flex-col gap-8">
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-full border border-secondary/20 bg-secondary/10 px-3 py-1 text-xs font-medium text-secondary uppercase tracking-widest font-sans">
                <Leaf className="mr-2 h-3.5 w-3.5" />
                Service Opportunities
              </div>
              <h2 className="text-3xl font-serif font-semibold tracking-tight sm:text-4xl md:text-5xl text-primary">
                Join Our Visitation Ministry
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                A beautiful life is one lived in service to others. Our dedicated team of volunteers provides consistent, high-quality companionship to keep our seniors feeling valued and loved. We handle the coordination so you can focus on making a difference.
              </p>
            </div>

            <div className="relative aspect-video w-full overflow-hidden rounded-xl shadow-2xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590036/img/st_sampson_volunteer.png" alt="Compassionate volunteer holding the hand of an elderly senior in a warm home setting" className="object-cover w-full h-full hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent pointer-events-none" />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              {maintenanceTasks.map((task, i) => <div key={i} className="flex gap-4" data-index={i}>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm border border-secondary/20 text-secondary">
                    <Check className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-serif font-semibold text-primary">{task.title}</h3>
                    <p className="text-sm text-muted-foreground leading-snug font-sans">
                      {task.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-sans">
                <SafeHydrate><Calendar className="h-4 w-4 text-secondary" /></SafeHydrate>
                <span>Flexible Hours</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-sans">
                <MapPin className="h-4 w-4 text-secondary" />
                <span>Local Visits</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-sans">
                <Info className="h-4 w-4 text-secondary" />
                <span>Background Checks Required</span>
              </div>
            </div>
          </div>

          
          <div className="lg:sticky lg:top-24">
            <Card className="border border-secondary/10 shadow-2xl bg-white/90 backdrop-blur-sm rounded-xl">
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl font-serif font-semibold text-primary">
                  Volunteer Application
                </CardTitle>
                <CardDescription className="font-sans">
                  Fill out the form below to begin your journey as a companion for seniors in need.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="volunteer_signup_form_volunteer_sign_up" formId="volunteer_signup_form_volunteer_sign_up" sectionName="volunteer-signup-form" defaultValues={{
                name: "",
                email: "",
                phone: "",
                address: "",
                property_type: "",
                service_type: [],
                preferred_start_date: "",
                additional_details: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                address: {
                  required: true
                },
                property_type: {
                  required: true
                },
                service_type: {
                  required: true
                }
              }} submitText="Submit Volunteer Application" successMessage="Thank you for your heart to serve! Your application has been received. Our coordinator will contact you shortly for an interview and background check.">
                  <LawnMaintenanceForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}